// Datenaufbewahrung — Fristen, Rechtsgrundlagen und Löschstatus je Datenart
// (Löschkonzept Kap. 8, docs/concepts/DATENAUFBEWAHRUNG_LOESCHKONZEPT.md).
//
// Einstellbare Fristen liegen in `appSettings/dataRetention` und werden nur
// über die Callable `configureDataRetention` geschrieben, die die Grenzen
// serverseitig prüft. Optionen und Standardwerte sind in
// nextcab_mobile/functions/src/retention/index.ts gespiegelt — synchron halten.
//
// `autoDelete` beschreibt ehrlich den Ist-Stand: false heißt, die Frist ist
// festgelegt, die automatische Löschung aber noch nicht gebaut — die Daten
// bleiben bis dahin gespeichert. Beim Ausbau (Löschkonzept 8.5) hier umstellen.

export const MIETWAGEN_MIN_RIDE_RETENTION_DAYS = 365;

const days = (value) => ({ value, label: value === 365 ? '1 Jahr' : value === 730 ? '2 Jahre' : `${value} Tage` });
const years = (value) => ({ value, label: `${value} Jahre` });

export const ADJUSTABLE_RETENTION = [
  {
    key: 'rideRetentionDays',
    label: 'Fahrten',
    description: 'Fahrten mit Adressen, Zeiten und Notizen (Fahrtenbuch, Umsatzanalyse).',
    options: [90, 120, 180, 365, 730].map(days),
    defaultValue: (unternehmensform) => (unternehmensform === 'mietwagen' ? MIETWAGEN_MIN_RIDE_RETENTION_DAYS : 120),
    legal: (unternehmensform) => (unternehmensform === 'mietwagen'
      ? 'Mietwagen: Aufträge mindestens 1 Jahr aufbewahren (§ 49 Abs. 4 PBefG).'
      : 'Taxi: mindestens 90 Tage (Fahrtenbuch, Zahlungsklärung).'),
    autoDelete: true
  },
  {
    key: 'receiptRetentionYears',
    label: 'Quittungen & Rechnungen',
    description: 'Quittungen, Rechnungen, Stornobelege und ihre PDFs — ab Ende des Ausstellungsjahres.',
    options: [8, 10].map(years),
    defaultValue: () => 8,
    legal: () => 'Gesetzlich 8 Jahre (§ 147 AO, § 14b UStG). Manche örtlichen Taxiordnungen verlangen 10 Jahre (z. B. LK Esslingen) — bitte prüfen.',
    autoDelete: false
  },
  {
    key: 'shiftRetentionYears',
    label: 'Schichten / Arbeitszeit',
    description: 'Beginn, Ende und Dauer der Schichten — ab Ende des Schichtjahres.',
    options: [2, 3, 6, 8].map(years),
    defaultValue: () => 2,
    legal: () => 'Mindestens 2 Jahre (§ 17 MiLoG, § 16 ArbZG). Länger nur, wenn die Schichten als Lohnbeleg dienen.',
    autoDelete: false
  },
  {
    key: 'driverTrackRetentionDays',
    label: 'GPS-Verlauf der Fahrer',
    description: 'Historische Standorte für Fahrer-Tracking und Streckenauswertung.',
    options: [60, 90].map(days),
    defaultValue: () => 60,
    legal: () => 'So kurz wie möglich (DSGVO); gilt für neue und vorhandene Standortsegmente.',
    autoDelete: true
  }
];

export const FIXED_RETENTION = [
  {
    label: 'Zahlungen (App/PayPal)',
    period: '180 Tage',
    legal: 'Käuferschutz- und Rückbuchungsfristen. Die Belege selbst gelten als Quittungen & Rechnungen.',
    autoDelete: true
  },
  {
    label: 'Chats & Benachrichtigungen',
    period: '60 Tage',
    legal: 'Datensparsamkeit (DSGVO).',
    autoDelete: true
  },
  {
    label: 'Anrufe & KI-Telefonist',
    period: '30 Tage',
    legal: 'Telefonnummern und Gesprächszusammenfassungen — Datensparsamkeit (DSGVO).',
    autoDelete: true
  },
  {
    label: 'Krankenfahrt-Unterlagen',
    period: '4 Jahre ab Ende des Abrechnungsjahres',
    legal: 'Verordnungen und Scans (Gesundheitsdaten, Art. 9 DSGVO) — Prüf- und Rückforderungsfenster der Kostenträger. Abrechnungsbelege gelten als Quittungen & Rechnungen.',
    autoDelete: false
  },
  {
    label: 'Firmen-Aktionen',
    period: '2 Jahre nach Aktionsende',
    legal: 'Einlöse-Protokolle mit Gastnamen als Nachweis gegenüber der Firma.',
    autoDelete: false
  },
  {
    label: 'Protokolle',
    period: '1 Jahr',
    legal: 'Audit- und Zugriffsprotokolle (GPS-Prüfung, Nachweise, Bestellknopf).',
    autoDelete: false
  },
  {
    label: 'Fahrer- und Fahrzeugdokumente',
    period: 'bis zur Löschung des Fahrers bzw. Fahrzeugs',
    legal: 'Werden mit dem Fahrer bzw. Fahrzeug gelöscht.',
    autoDelete: true
  },
  {
    label: 'Kundenkonten',
    period: 'bis zur Kontolöschung',
    legal: 'Kunden löschen ihr Konto selbst in der App; Fahrten und Belege folgen ihren eigenen Fristen.',
    autoDelete: true
  }
];

/**
 * Wirksame Werte wie in der Cloud Function: Fehlt `appSettings/dataRetention`,
 * gelten die bisherigen Keys aus `appSettings/cloudfunctions`; Mietwagen nie
 * unter 365 Tage, Taxi nie unter 90.
 */
export const resolveRetentionSettings = (retentionDoc, cloudFunctionSettings, unternehmensform) => {
  const pick = (value, config) => {
    const numeric = Number(value);
    return config.options.some(option => option.value === numeric) ? numeric : config.defaultValue(unternehmensform);
  };
  const byKey = Object.fromEntries(ADJUSTABLE_RETENTION.map(config => [config.key, config]));
  const ride = pick(retentionDoc?.rideRetentionDays ?? cloudFunctionSettings?.rideRetentionDays, byKey.rideRetentionDays);
  return {
    rideRetentionDays: Math.max(unternehmensform === 'mietwagen' ? MIETWAGEN_MIN_RIDE_RETENTION_DAYS : 90, ride),
    receiptRetentionYears: pick(retentionDoc?.receiptRetentionYears, byKey.receiptRetentionYears),
    shiftRetentionYears: pick(retentionDoc?.shiftRetentionYears, byKey.shiftRetentionYears),
    driverTrackRetentionDays: pick(
      retentionDoc?.driverTrackRetentionDays ?? cloudFunctionSettings?.driverTrackRetentionDays,
      byKey.driverTrackRetentionDays
    )
  };
};
